import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { UserPlus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input, inputClassName } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Spinner } from '@/components/ui/spinner'
import TelephoneNumberInput from '../../Form/TelephoneNumberInput'
import { EventTypeLocation, EventTypeLocationType } from '../../../types'
import { isAttendeeLocation, isPhoneLocation, LocationIcon } from '../locations'
import { locationLabel } from './format'

export interface BookingFormValues {
  name: string
  email: string
  notes: string
  guests: string[]
  location: EventTypeLocationType | null
  location_value: string
}

interface IProps {
  locations: EventTypeLocation[]
  initialValues?: Partial<BookingFormValues>
  disabled?: boolean
  submitting: boolean
  errors: Record<string, string>
  generalError?: string | null
  onBack: () => void
  onValuesChange?: (values: BookingFormValues) => void
  onSubmit: (values: BookingFormValues) => void
}

const MAX_GUESTS = 10

export default function BookingForm({ locations, initialValues, disabled, submitting, errors, generalError, onBack, onValuesChange, onSubmit }: IProps) {
  const { t } = useTranslation()
  // The Booker mounts a wide and a narrow layout; unique ids keep label associations correct.
  const uid = React.useId()
  const [values, setValues] = React.useState<BookingFormValues>({
    name: '', email: '', notes: '', guests: [], location: locations[0]?.type || null, location_value: '', ...initialValues,
  })
  const [showGuests, setShowGuests] = React.useState(values.guests.length > 0)
  const [guestInput, setGuestInput] = React.useState('')

  // Lets the Booker keep a draft so the form survives a layout switch (remount)
  React.useEffect(() => { onValuesChange?.(values) }, [values])

  const addGuest = () => {
    const email = guestInput.trim().toLowerCase()
    if (!email || values.guests.includes(email) || values.guests.length >= MAX_GUESTS) return
    setValues(v => ({ ...v, guests: [...v.guests, email] }))
    setGuestInput('')
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (disabled) return
    const pending = guestInput.trim().toLowerCase()
    const guests = pending && !values.guests.includes(pending) && values.guests.length < MAX_GUESTS ? [...values.guests, pending] : values.guests
    onSubmit({ ...values, guests })
  }

  return (
    <form onSubmit={submit} className='flex flex-col gap-4'>
      <Field data-invalid={!!errors.name} className='gap-2'>
        <FieldLabel htmlFor={`${uid}-name`}>{t('Scheduling::Your name')} *</FieldLabel>
        <Input id={`${uid}-name`} autoComplete='name' required maxLength={100} disabled={disabled} className='w-full!'
          value={values.name} onChange={e => setValues(v => ({ ...v, name: e.target.value }))} />
        {errors.name && <FieldError>{t('Scheduling::Please enter your name')}</FieldError>}
      </Field>

      <Field data-invalid={!!errors.email} className='gap-2'>
        <FieldLabel htmlFor={`${uid}-email`}>{t('Scheduling::Email address')} *</FieldLabel>
        <Input id={`${uid}-email`} type='email' inputMode='email' autoComplete='email' required disabled={disabled} className='w-full!'
          value={values.email} onChange={e => setValues(v => ({ ...v, email: e.target.value }))} />
        {errors.email && <FieldError>{t('Scheduling::Please enter a valid email address')}</FieldError>}
      </Field>

      {locations.length > 1 && (
        <Field className='gap-2'>
          <FieldLabel>{t('Scheduling::Location')}</FieldLabel>
          <ToggleGroup
            variant='outline'
            orientation='vertical'
            className='w-full'
            disabled={disabled}
            value={values.location ? [values.location] : []}
            onValueChange={(v: string[]) => v[0] && setValues(prev => ({ ...prev, location: v[0] as EventTypeLocationType, location_value: '' }))}
          >
            {locations.map(location => (
              <ToggleGroupItem key={location.type} value={location.type} className='justify-start'>
                <LocationIcon type={location.type} className='size-4' />
                {locationLabel(t, location.type)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </Field>
      )}

      {isAttendeeLocation(values.location) && (
        <Field data-invalid={!!errors.location_value} className='gap-2'>
          <FieldLabel htmlFor={`${uid}-location-value`}>
            {isPhoneLocation(values.location) ? t('Scheduling::Phone number') : t('Scheduling::Address')} *
          </FieldLabel>
          {isPhoneLocation(values.location) ? (
            <TelephoneNumberInput
              // Remount when switching locations so intl-tel-input picks up the cleared value
              key={values.location}
              initialValue={values.location_value}
              disabled={disabled}
              onChangeNumber={number => setValues(v => ({ ...v, location_value: number }))}
              inputProps={{ id: `${uid}-location-value`, required: true, autoComplete: 'tel', className: inputClassName, 'aria-invalid': !!errors.location_value }}
            />
          ) : (
            <Input id={`${uid}-location-value`} autoComplete='street-address' required maxLength={500} disabled={disabled} className='w-full!'
              value={values.location_value} onChange={e => setValues(v => ({ ...v, location_value: e.target.value }))} />
          )}
          {errors.location_value && (
            <FieldError>
              {isPhoneLocation(values.location) ? t('Scheduling::Please enter a valid phone number') : t('Scheduling::Please enter an address')}
            </FieldError>
          )}
        </Field>
      )}

      <Field data-invalid={!!errors.notes} className='gap-2'>
        <FieldLabel htmlFor={`${uid}-notes`}>{t('Scheduling::Additional notes')}</FieldLabel>
        <Textarea id={`${uid}-notes`} rows={3} maxLength={5000} disabled={disabled} className='w-full!'
          placeholder={t('Scheduling::Please share anything that will help prepare for our meeting.')}
          value={values.notes} onChange={e => setValues(v => ({ ...v, notes: e.target.value }))} />
        {errors.notes && <FieldError>{t('Scheduling::Notes are too long or invalid')}</FieldError>}
      </Field>

      {!showGuests ? (
        <Button type='button' variant='ghost' className='self-start text-muted-foreground' onClick={() => setShowGuests(true)} disabled={disabled}>
          <UserPlus className='size-4' /> {t('Scheduling::Add guests')}
        </Button>
      ) : (
        <Field data-invalid={!!errors.guests} className='gap-2'>
          <FieldLabel htmlFor={`${uid}-guest`}>{t('Scheduling::Guests')}</FieldLabel>
          <div className='flex flex-wrap gap-1'>
            {values.guests.map(guest => (
              <span key={guest} className='inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-sm'>
                {guest}
                <button type='button' aria-label={t('Scheduling::Remove guest')} className='-mr-1 inline-flex size-5 items-center justify-center'
                  onClick={() => setValues(v => ({ ...v, guests: v.guests.filter(g => g !== guest) }))}>
                  <X className='size-3' />
                </button>
              </span>
            ))}
          </div>
          <div className='flex gap-2'>
            <Input id={`${uid}-guest`} type='email' inputMode='email' className='w-full!' value={guestInput} disabled={disabled || values.guests.length >= MAX_GUESTS}
              onChange={e => setGuestInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addGuest() } }} />
            <Button type='button' variant='outline' onClick={addGuest} disabled={disabled}>{t('Scheduling::Add')}</Button>
          </div>
          {errors.guests && <FieldError>{t('Scheduling::Please enter valid guest emails (max 10)')}</FieldError>}
        </Field>
      )}

      {generalError && <p role='alert' className='text-sm text-destructive'>{generalError}</p>}

      <div className='mt-4 flex items-center justify-end gap-2'>
        <Button type='button' variant='ghost' className='text-muted-foreground' onClick={onBack}>{t('Scheduling::Back')}</Button>
        <Button type='submit' disabled={submitting || disabled}>
          {submitting && <Spinner />} {t('Scheduling::Confirm')}
        </Button>
      </div>
    </form>
  )
}
